import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { InventoryAdjustmentTableDTO } from "../../../types/inventory/adjustment/InventoryAdjustmentTypes";

/**
 * Estado global para los AJUSTES de inventario (consulta).
 */
interface InventoryAdjustmentQueryState {
  // ────────────────────────────────────────────
  // Tabla principal
  // ────────────────────────────────────────────
  list: InventoryAdjustmentTableDTO[];
  paginator: any;

  selectedAdjustment: InventoryAdjustmentTableDTO | null;

  // ────────────────────────────────────────────
  // Ajustes pendientes
  // ────────────────────────────────────────────
  pendingList: InventoryAdjustmentTableDTO[];
  pendingCount: number;

  // ────────────────────────────────────────────
  // UI / estado global
  // ────────────────────────────────────────────
  isLoading: boolean;
  error: string | null;

  showAdjustmentModal: boolean;
  showPendingAdjustmentModal: boolean;
  showAdjustmentDetailModal: boolean;
}

/**
 * Estado inicial.
 */
const initialState: InventoryAdjustmentQueryState = {
  // Tabla principal
  list: [],
  paginator: null,
  selectedAdjustment: null,

  // Pendientes
  pendingList: [],
  pendingCount: 0,

  // UI
  isLoading: false,
  error: null,
  showAdjustmentModal: false,
  showPendingAdjustmentModal: false,
  showAdjustmentDetailModal: false,
};

const inventoryAdjustmentQuerySlice = createSlice({
  name: "inventoryAdjustmentQuery",
  initialState,
  reducers: {
    // ────────────────────────────────────────────
    // TABLA PRINCIPAL
    // ────────────────────────────────────────────
    setAdjustmentsList(
      state,
      action: PayloadAction<{
        content: InventoryAdjustmentTableDTO[];
        paginator: any;
      }>
    ) {
      state.list = action.payload.content;
      state.paginator = action.payload.paginator;
    },

    // ────────────────────────────────────────────
    // ACTUALIZAR ESTADO DE AJUSTE (POR ID)
    // ────────────────────────────────────────────
    updateAdjustmentStatus(
      state,
      action: PayloadAction<{ id: number; status: string }>
    ) {
      const { id, status } = action.payload;

      // Actualizar en la tabla principal
      const item = state.list.find((a) => a.id === id);
      if (item) {
        item.status = status;
      }
    },


    clearAdjustmentsList(state) {
      state.list = [];
      state.paginator = null;
    },

    setSelectedAdjustment(
      state,
      action: PayloadAction<InventoryAdjustmentTableDTO | null>
    ) {
      state.selectedAdjustment = action.payload;
    },

    // ────────────────────────────────────────────
    // AJUSTES PENDIENTES
    // ────────────────────────────────────────────
    setPendingAdjustments(
      state,
      action: PayloadAction<InventoryAdjustmentTableDTO[]>
    ) {
      state.pendingList = action.payload;
    },

    clearPendingAdjustments(state) {
      state.pendingList = [];
    },

    setPendingAdjustmentsCount(
      state,
      action: PayloadAction<number>
    ) {
      state.pendingCount = action.payload;
    },

    // ────────────────────────────────────────────
    // UI / ESTADO GLOBAL
    // ────────────────────────────────────────────
    setIsLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },

    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },

    openAdjustmentModal(state) {
      state.showAdjustmentModal = true;
    },

    closeAdjustmentModal(state) {
      state.showAdjustmentModal = false;
    },

    // 🆕 MODAL DE PENDIENTES
    openPendingAdjustmentModal(state) {
      state.showPendingAdjustmentModal = true;
    },

    closePendingAdjustmentModal(state) {
      state.showPendingAdjustmentModal = false;
    },

    // 🆕 MODAL DETALLE
    openAdjustmentDetailModal(state) {
      state.showAdjustmentDetailModal = true;
    },

    closeAdjustmentDetailModal(state) {
      state.showAdjustmentDetailModal = false;
    },
  },
});

// Exportar acciones
export const {
  setAdjustmentsList,
  updateAdjustmentStatus,
  clearAdjustmentsList,
  setSelectedAdjustment,

  setPendingAdjustments,
  clearPendingAdjustments,
  setPendingAdjustmentsCount,

  setIsLoading,
  setError,
  openAdjustmentModal,
  closeAdjustmentModal,
  openPendingAdjustmentModal,   // 🆕
  closePendingAdjustmentModal,  // 🆕

  // 🆕 Detalle
  openAdjustmentDetailModal,
  closeAdjustmentDetailModal,
} = inventoryAdjustmentQuerySlice.actions;

// Reducer
export default inventoryAdjustmentQuerySlice.reducer;
